import { useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Track, ParticipantEvent, TrackPublication } from 'livekit-client';
import { selectLocalParticipant } from 'state/selectors';
import { useUserSettingsContext } from 'contexts';
import { IN_ROOM, AUDIO_CONSTRAINTS } from 'consts';

// Коэффициент для регулировки чувствительности индикатора (чем выше, тем чувствительнее).
// Подобран эмпирическим путём.
const INDICATOR_SENSITIVITY = 4;

const getActiveMicrophone = async (preferredDeviceId?: string): Promise<MediaStream | null> => {
    try {
        // С AUDIO_CONSTRAINTS уровень в индикаторе будет ближе к тому, что реально слышит мит.
        // Без preferredDeviceId браузер сам подбирает системное устройство по умолчанию.
        const audioConstraints: MediaTrackConstraints = preferredDeviceId
            ? { ...AUDIO_CONSTRAINTS, deviceId: { exact: preferredDeviceId } }
            : { ...AUDIO_CONSTRAINTS };
        return await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    } catch (e) {
        console.error(e);
        return null;
    }
};

type UseAudioVolumeProps = {
    /** Вызывается на каждом значимом изменении уровня (0–1). Хук сам state не хранит. */
    onLevelChange: (level: number) => void;
};

export const useAudioVolume = ({ onLevelChange }: UseAudioVolumeProps) => {
    // Latest-ref на коллбэк: trackAudioLevel мемоизирован пустыми deps,
    // а onLevelChange у консумера может меняться с каждым рендером — через ref
    // tick всегда читает последнюю версию, не пересоздавая subscriber'ов.
    const onLevelChangeRef = useRef(onLevelChange);
    useLayoutEffect(() => {
        onLevelChangeRef.current = onLevelChange;
    });

    // Гейтимся на permissions.audio, а не на audioEnabled: audioEnabled — это mute/unmute,
    // и завязка на него тушила бы capture на каждый mute. permissions.audio — реальное
    // разрешение на микрофон, его и проверяем.
    const { permissions, audioInputDeviceId } = useUserSettingsContext();
    const hasAudioPermission = permissions.audio;

    const localParticipant = selectLocalParticipant();

    const inRoomNow = Boolean(sessionStorage.getItem(IN_ROOM));

    // MediaStream нашего собственного getUserMedia. Поднимается в pre-join и в in-room до публикации
    // LiveKit-трека (например, заход в room в mute). Когда LiveKit публикует свой трек, мы переезжаем
    // на клон его mediaStreamTrack и эту ручку отпускаем — capture с LiveKit делится.
    const ownStreamRef = useRef<MediaStream | null>(null);

    // Хранит функцию остановки rAF-цикла и закрытия AudioContext.
    // Вызывается перед каждым новым запуском трекинга и при размонтировании.
    const cleanupTrackingRef = useRef<(() => void) | null>(null);

    const trackAudioLevel = useCallback((stream: MediaStream) => {
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();

        // fftSize 256 даёт 128 частотных бинов — достаточно для измерения громкости (по умолчанию 2048)
        analyser.fftSize = 256;
        source.connect(analyser);

        const data = new Uint8Array(analyser.frequencyBinCount);
        let rafId: number;
        let frameCount = 0;
        let prevLevel = 0;

        const tick = () => {
            // rAF планируется в начале, чтобы cancelAnimationFrame при размонтировании
            // всегда мог отменить следующий кадр, даже если текущий был пропущен.
            rafId = requestAnimationFrame(tick);

            // Throttle до ~15 fps: пропускаем 3 из 4 кадров.
            // Для визуального индикатора громкости 60 fps избыточны.
            if (frameCount++ % 4 !== 0) return;

            // getByteFrequencyData читает уже готовые FFT-данные из аудио-потока браузера —
            // сам FFT вычисляется не в JS, поэтому операция дешёвая.
            analyser.getByteFrequencyData(data);

            // Значения каждого бина: 0–255, усредняем и нормализуем в диапазон 0–1.
            const level = Math.min((data.reduce((a, b) => a + b, 0) / data.length / 255) * INDICATOR_SENSITIVITY, 1);

            // Вызываем коллбэк только при значимом изменении уровня,
            // чтобы не дёргать консумера в тишине или при ровном голосе.
            if (Math.abs(level - prevLevel) > 0.01) {
                prevLevel = level;
                onLevelChangeRef.current(level < 0.01 ? 0 : level);
            }
        };

        tick();

        return () => {
            cancelAnimationFrame(rafId);
            audioContext.close();
        };
    }, []);

    const handleLocalTrackPublished = useCallback(
        (publication: TrackPublication | undefined) => {
            if (publication?.source !== Track.Source.Microphone) return;
            const mediaStreamTrack = publication?.audioTrack?.mediaStreamTrack;
            if (!mediaStreamTrack) return;
            // Останавливаем предыдущий трекинг (вместе с прошлым клоном) перед запуском нового,
            // чтобы не накапливать AudioContext, rAF-циклы и висящие клоны.
            cleanupTrackingRef.current?.();
            // Если до этого мы крутились на собственном getUserMedia (заход в room в mute, до публикации) —
            // отпускаем эту ручку. После появления LiveKit-публикации можем переехать на клон её трека,
            // capture с LiveKit делится, второй handle к устройству нам больше не нужен.
            ownStreamRef.current?.getTracks().forEach((track) => track.stop());
            ownStreamRef.current = null;
            // Читаем уровень не с самого LiveKit-трека, а с его клона.
            // У клонированного MediaStreamTrack флаг enabled живёт независимо от оригинала,
            // поэтому когда LiveKit мьютит микрофон (enabled = false на оригинале),
            // клон продолжает выдавать живой звук с того же физического capture —
            // индикатор реагирует даже при mute в мите. Второй getUserMedia не поднимаем:
            // capture у нас один с LiveKit, AEC-контекст общий, в индикатор не утечёт speaker bleed.
            const clone = mediaStreamTrack.clone();
            clone.enabled = true;
            const stopTracking = trackAudioLevel(new MediaStream([clone]));
            cleanupTrackingRef.current = () => {
                stopTracking();
                clone.stop();
            };
        },
        [trackAudioLevel],
    );

    useEffect(
        function acquireAndTrackMicLevel() {
            // Сбрасываем индикатор в 0 (при переключении устройства).
            onLevelChangeRef.current(0);

            // Флаг защищает от race condition: если компонент размонтируется
            // до того, как getActiveMicrophone() зарезолвится, поток не запустится.
            let cancelled = false;

            if (hasAudioPermission) {
                // В звонке подписываемся всегда: ловим (а) появление публикации после захода в room в mute
                // и (б) republish трека (например, смена устройства in-room).
                if (inRoomNow) {
                    localParticipant?.on(ParticipantEvent.LocalTrackPublished, handleLocalTrackPublished);
                }

                const setup = async () => {
                    const publication = localParticipant?.getTrackPublication(Track.Source.Microphone);

                    if (publication) {
                        // Микрофон уже опубликован к моменту монтирования — сразу заходим на клон.
                        handleLocalTrackPublished(publication);
                        return;
                    }

                    // Публикации нет: pre-join, либо заход в room с замьюченным микрофоном.
                    // Открываем свой getUserMedia, чтобы индикатор реагировал. Когда позже
                    // (на unmute или device-switch) LiveKit опубликует трек — LocalTrackPublished
                    // переключит нас на клон и отпустит ownStreamRef.
                    const stream = await getActiveMicrophone(audioInputDeviceId);

                    // Пока ждали getUserMedia, могло прилететь LocalTrackPublished (быстрый unmute) —
                    // тогда cleanupTrackingRef уже занят клоном, не перетираем его нашим стримом.
                    if (cancelled || cleanupTrackingRef.current) {
                        stream?.getTracks().forEach((t) => t.stop());
                        return;
                    }
                    if (!stream) return;
                    ownStreamRef.current = stream;
                    cleanupTrackingRef.current = trackAudioLevel(stream);
                };
                setup();
            }

            return () => {
                cancelled = true;
                localParticipant?.off(ParticipantEvent.LocalTrackPublished, handleLocalTrackPublished);
                cleanupTrackingRef.current?.();
                cleanupTrackingRef.current = null;
                // ownStreamRef — наш собственный getUserMedia, обязаны стопнуть.
                // Клон LiveKit-трека стопается внутри cleanupTrackingRef. Сам LiveKit-трек не трогаем.
                ownStreamRef.current?.getTracks().forEach((track) => track.stop());
                ownStreamRef.current = null;
            };
        },
        [localParticipant, handleLocalTrackPublished, trackAudioLevel, hasAudioPermission, audioInputDeviceId],
    );
};
